import { useState } from 'react';
import { Button, H1, Input, Paragraph, Text, XStack, YStack } from 'tamagui';
import { Screen, SkeletonList } from '@mrrmlab/ui';
import type { Tracking } from '@mrrmlab/shared-types';
import { useCreateTracking, useTrackings, useTriggerTrackingSync } from '../hooks';

export interface TrackingListScreenProps {
  /** Called when the user taps a tracking — apps route to the detail screen. */
  onOpenTracking?: (tracking: Tracking) => void;
}

export function TrackingListScreen({ onOpenTracking }: TrackingListScreenProps) {
  const query = useTrackings();

  return (
    <Screen>
      <H1>Pakete</H1>

      <XStack gap="$2" alignItems="flex-start" flexWrap="wrap">
        <AddTrackingForm />
        <SyncTrackingsButton />
      </XStack>

      {query.isPending ? (
        <SkeletonList count={5} />
      ) : query.isError ? (
        <Paragraph color="$red10">
          Pakete konnten nicht geladen werden: {query.error.message}
        </Paragraph>
      ) : query.data.length === 0 ? (
        <Paragraph opacity={0.7}>Noch keine Pakete erfasst.</Paragraph>
      ) : (
        <YStack gap="$2">
          {query.data.map((t) => (
            <XStack
              key={t.id}
              alignItems="center"
              gap="$3"
              paddingVertical="$3"
              paddingHorizontal="$3"
              borderRadius="$3"
              backgroundColor="$background"
              borderWidth={1}
              borderColor="$borderColor"
              hoverStyle={{ backgroundColor: '$backgroundHover' }}
              pressStyle={onOpenTracking ? { opacity: 0.8 } : undefined}
              onPress={onOpenTracking ? () => onOpenTracking(t) : undefined}
              cursor={onOpenTracking ? 'pointer' : undefined}
            >
              <CarrierBadge carrier={t.carrier} />
              <YStack flex={1} gap="$1">
                <Paragraph fontSize="$5" fontWeight="600">
                  {t.trackingNumber}
                </Paragraph>
                {t.orderInfo?.shopName ? (
                  <Text fontSize="$2" opacity={0.7}>
                    {t.orderInfo.shopName}
                    {t.orderInfo.orderNumber ? ` · ${t.orderInfo.orderNumber}` : ''}
                  </Text>
                ) : null}
              </YStack>
              <StatusBadge status={t.status} />
            </XStack>
          ))}
        </YStack>
      )}
    </Screen>
  );
}

function CarrierBadge({ carrier }: { carrier: string | null }) {
  const label = (carrier ?? '?').toUpperCase().slice(0, 3);
  return (
    <XStack
      paddingHorizontal="$2"
      paddingVertical="$1"
      borderRadius="$2"
      backgroundColor="$color3"
      minWidth={36}
      justifyContent="center"
    >
      <Text fontSize="$2" letterSpacing={0.5}>
        {label}
      </Text>
    </XStack>
  );
}

function AddTrackingForm() {
  const [open, setOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateTracking();

  if (!open) {
    return (
      <XStack>
        <Button size="$3" onPress={() => setOpen(true)}>
          + Paket
        </Button>
      </XStack>
    );
  }

  const reset = () => {
    setTrackingNumber('');
    setCarrier('');
    setNote('');
    setError(null);
  };

  const submit = () => {
    const tn = trackingNumber.trim();
    if (tn.length === 0) {
      setError('Tracking-Nummer ist erforderlich');
      return;
    }
    setError(null);
    create.mutate(
      {
        trackingNumber: tn,
        ...(carrier.trim() ? { carrier: carrier.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <YStack
      gap="$2"
      padding="$3"
      borderWidth={1}
      borderColor="$borderColor"
      borderRadius="$3"
      backgroundColor="$background"
    >
      <Text fontWeight="600">Paket manuell anlegen</Text>
      <Input
        placeholder="Tracking-Nummer"
        value={trackingNumber}
        onChangeText={setTrackingNumber}
        autoCapitalize="characters"
      />
      <Input
        placeholder="Carrier (optional, z.B. dhl, ups, hermes)"
        value={carrier}
        onChangeText={setCarrier}
        autoCapitalize="none"
      />
      <Input placeholder="Notiz (optional)" value={note} onChangeText={setNote} />
      {error ? (
        <Paragraph color="$red10" fontSize="$2">
          {error}
        </Paragraph>
      ) : null}
      <XStack gap="$2" justifyContent="flex-end">
        <Button
          size="$3"
          chromeless
          onPress={() => {
            reset();
            setOpen(false);
          }}
          disabled={create.isPending}
        >
          Abbrechen
        </Button>
        <Button size="$3" onPress={submit} disabled={create.isPending}>
          {create.isPending ? 'Anlegen…' : 'Anlegen'}
        </Button>
      </XStack>
    </YStack>
  );
}

function SyncTrackingsButton() {
  const sync = useTriggerTrackingSync();
  const [message, setMessage] = useState<string | null>(null);

  const trigger = () => {
    setMessage(null);
    sync.mutate(undefined, {
      onSuccess: (r) =>
        setMessage(`Sync OK · neu registriert: ${r.registered} · aktualisiert: ${r.updated}`),
      onError: (err) => setMessage(`Sync fehlgeschlagen: ${err.message}`),
    });
  };

  return (
    <YStack gap="$1">
      <Button size="$3" onPress={trigger} disabled={sync.isPending} chromeless>
        {sync.isPending ? 'Sync läuft…' : '↻ Sync'}
      </Button>
      {message ? (
        <Text fontSize="$2" opacity={0.7}>
          {message}
        </Text>
      ) : null}
    </YStack>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  const display = status ?? 'Pending';
  return (
    <XStack
      paddingHorizontal="$2"
      paddingVertical="$1"
      borderRadius="$2"
      backgroundColor="$color4"
    >
      <Text fontSize="$2">{display}</Text>
    </XStack>
  );
}
